// =====================================================================================
// FILE: src/components/layout/Header.jsx
// Two-tier header with main header and subheader
// =====================================================================================

import React, { useMemo } from "react";
import { THEME, FONTS } from "../../constants/theme";
import { PresenceAvatars } from "../presence";
import { useMatchLock } from "../../AppShell";

// ✅ Default Team List view
const DEFAULT_TEAM_VIEW = "MCG Team List";

export default function Header({
  fixtureKey,
  viewName,
  viewOptions,
  isTeam,
  isTvc,
  tvcHeaderInfo,
  onTvcVenueChange,
  isCommLed,
  ledHeaderInfo,
  onLedVenueChange,
  modeLabel,
  statusLine,
  onViewChange,
  onNewRow,
  baseParams,
  tableNames,
  // ✅ NEW: Props for subheader content
  saving,
  lastSaved,
  searchTerm,
  onSearchChange,
  searchMatchCount,
  onOpenGallery,
  onAddContent, // ✅ NEW: Callback to open Add Content modal
  onUndo, // ✅ NEW: Callback to undo last action
  canUndo, // ✅ NEW: Whether undo is available
  onCloneMatch, // ✅ NEW: Callback to open Clone Match modal
  onBulkSwap, // ✅ NEW: Callback to open Bulk Asset Swap modal
}) {
  // ✅ Determine dropdown label based on mode
  const dropdownLabel = isTeam ? "Venue:" : "Select Match:";

  return (
    <div style={{
      overflow: "hidden",
    }}>
      {/* ========== MAIN HEADER ========== */}
      <div style={{
        background: "#111114",
        color: THEME.textOnDark,
        padding: "14px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 20,
        fontFamily: FONTS.family,
      }}>
        {/* Left: Title only (logo is in sidebar now) */}
        <div>
          <div style={{
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}>
            MatchFlow
          </div>
          <div style={{
            fontSize: 13,
            opacity: 0.5,
            marginTop: 2,
          }}>
            Venue Content Scheduling Dashboard
          </div>
        </div>

        {/* Right: Presence + Mode Selector */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 16,
        }}>
          {/* Live Collaboration Indicator */}
          <PresenceAvatars maxVisible={3} />
          
          {/* Mode Selector */}
          <ModeSelector baseParams={baseParams} modeLabel={modeLabel} tableNames={tableNames} />
        </div>
      </div>

      {/* ========== TEAL ACCENT LINE ========== */}
      <div style={{
        height: 3,
        background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
      }} />

      {/* ========== SUBHEADER ========== */}
      <div style={{
        background: "#1a1a1d",
        color: THEME.textOnDark,
        padding: "12px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        fontFamily: FONTS.family,
      }}>
        {/* Left: Sync Status & Fixture Selector */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}>
          {/* Sync Status */}
          <SyncStatus saving={saving} lastSaved={lastSaved} />

          {/* Divider */}
          <div style={{
            width: 1,
            height: 24,
            background: "rgba(255,255,255,0.15)",
          }} />

          {/* Fixture/View Selector — hidden in TVC and LED playlist modes */}
          {!isTvc && !isCommLed && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <span style={{
              fontSize: FONTS.sm,
              opacity: 0.6,
            }}>
              {dropdownLabel}
            </span>
            <select
              value={viewName}
              onChange={(e) => onViewChange(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: THEME.radius,
                color: "white",
                fontSize: FONTS.sm,
                fontWeight: FONTS.medium,
                fontFamily: FONTS.family,
                padding: "8px 12px",
                cursor: "pointer",
                outline: "none",
                minWidth: 200,
              }}
              title={isTeam ? "Select Team List venue" : "Select fixture"}
            >
              {viewOptions.map((o) => (
                <option key={o.value} value={o.value} style={{ color: THEME.dark }}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* TVC Mode: Round dropdown + info text */}
          {isTvc && tvcHeaderInfo && (
            <>
              <select
                value={tvcHeaderInfo.selectedRound || "ALL"}
                onChange={(e) => onTvcVenueChange && onTvcVenueChange(e.target.value)}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: THEME.radiusSm,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: FONTS.sm,
                  fontWeight: FONTS.medium,
                  fontFamily: FONTS.family,
                  padding: "6px 28px 6px 10px",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  WebkitAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='rgba(255,255,255,0.5)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                }}
              >
                <option value="ALL" style={{ color: THEME.dark }}>All Rounds</option>
                {(tvcHeaderInfo.rounds || []).map((r) => (
                  <option key={r} value={r} style={{ color: THEME.dark }}>{r}</option>
                ))}
              </select>
              {tvcHeaderInfo.infoText && (
                <>
                  <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.15)" }} />
                  <span style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.5)" }}>
                    {tvcHeaderInfo.infoText}
                  </span>
                </>
              )}
            </>
          )}

          {/* LED Playlist Mode: Round dropdown + info text */}
          {isCommLed && ledHeaderInfo && (
            <>
              <select
                value={ledHeaderInfo.selectedRound || "ALL"}
                onChange={(e) => onLedVenueChange && onLedVenueChange(e.target.value)}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: THEME.radiusSm,
                  color: "rgba(255,255,255,0.85)",
                  fontSize: FONTS.sm,
                  fontWeight: FONTS.medium,
                  fontFamily: FONTS.family,
                  padding: "6px 28px 6px 10px",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  WebkitAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='rgba(255,255,255,0.5)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                }}
              >
                <option value="ALL" style={{ color: THEME.dark }}>All Rounds</option>
                {(ledHeaderInfo.rounds || []).map((r) => (
                  <option key={r} value={r} style={{ color: THEME.dark }}>{r}</option>
                ))}
              </select>
              {ledHeaderInfo.infoText && (
                <>
                  <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.15)" }} />
                  <span style={{ fontSize: FONTS.sm, color: "rgba(255,255,255,0.5)" }}>
                    {ledHeaderInfo.infoText}
                  </span>
                </>
              )}
            </>
          )}

          {/* Status Line (if any) */}
          {statusLine && (
            <>
              <div style={{
                width: 1,
                height: 24,
                background: "rgba(255,255,255,0.15)",
              }} />
              <span style={{
                fontSize: FONTS.sm,
                opacity: 0.7,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}>
                {statusLine}
              </span>
            </>
          )}
        </div>

        {/* Right: Add Content + Gallery + Search — hidden in TVC and LED Playlist modes */}
        {!isTvc && !isCommLed && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Add Content Button */}
          {onAddContent && (
            <button
              onClick={onAddContent}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
                border: "none",
                borderRadius: THEME.radius,
                cursor: "pointer",
                color: "#111",
                fontSize: FONTS.sm,
                fontWeight: FONTS.semibold,
                fontFamily: FONTS.family,
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "brightness(1.05)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(47, 214, 181, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "brightness(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
              title="Add new content row"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add Content</span>
            </button>
          )}

          {/* Gallery Button */}
          {onOpenGallery && (
            <button
              onClick={onOpenGallery}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: THEME.radius,
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
                fontSize: FONTS.sm,
                fontFamily: FONTS.family,
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = THEME.primary;
                e.currentTarget.style.background = "rgba(35,201,182,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
              title="Browse asset gallery"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              <span>Gallery</span>
            </button>
          )}

          {/* Search */}
          <SubheaderSearch 
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            matchCount={searchMatchCount}
          />

          {/* Clone Match Button */}
          {onCloneMatch && (
            <button
              onClick={onCloneMatch}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: THEME.radius,
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
                fontSize: FONTS.sm,
                fontWeight: FONTS.medium,
                fontFamily: FONTS.family,
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
              title="Clone rows from a previous match"
            >
              <span style={{ fontSize: 13 }}>📋</span>
              <span>Clone Match</span>
            </button>
          )}

          {/* Bulk Asset Swap Button */}
          {onBulkSwap && (
            <button
              onClick={onBulkSwap}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: THEME.radius,
                cursor: "pointer",
                color: "rgba(255,255,255,0.8)",
                fontSize: FONTS.sm,
                fontWeight: FONTS.medium,
                fontFamily: FONTS.family,
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
              title="Replace an asset across all rows"
            >
              <span style={{ fontSize: 13 }}>🔄</span>
              <span>Swap Asset</span>
            </button>
          )}

          {/* Undo Button */}
          {onUndo && (
            <button
              onClick={onUndo}
              disabled={!canUndo}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                background: canUndo ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: THEME.radius,
                cursor: canUndo ? "pointer" : "not-allowed",
                color: canUndo ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)",
                fontSize: FONTS.sm,
                fontWeight: FONTS.medium,
                fontFamily: FONTS.family,
                transition: "all 0.15s ease",
                opacity: canUndo ? 1 : 0.5,
              }}
              onMouseEnter={(e) => {
                if (canUndo) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                }
              }}
              onMouseLeave={(e) => {
                if (canUndo) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }
              }}
              title={canUndo ? "Undo last action" : "Nothing to undo"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7v6h6" />
                <path d="M3 13a9 9 0 1 0 2.5-6.5L3 7" />
              </svg>
              <span>Undo</span>
            </button>
          )}
        </div>
        )}
      </div>

      {/* ========== SUBHEADER BOTTOM ACCENT LINE ========== */}
      <div style={{
        height: 3,
        background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
      }} />
    </div>
  );
}

// =====================================================================================
// SYNC STATUS SUB-COMPONENT (for subheader)
// =====================================================================================

function SyncStatus({ saving, lastSaved }) {
  if (saving) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: FONTS.sm,
        color: "rgba(255,255,255,0.8)",
      }}>
        <div style={{
          width: 12,
          height: 12,
          border: "2px solid rgba(255,255,255,0.3)",
          borderTopColor: THEME.primary,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <span>Saving...</span>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: FONTS.sm,
      color: "rgba(255,255,255,0.7)",
    }}>
      <span style={{ 
        width: 8, 
        height: 8, 
        background: THEME.primary, 
        borderRadius: "50%",
        boxShadow: `0 0 6px ${THEME.primary}`,
      }} />
      <span>Synced</span>
    </div>
  );
}

// =====================================================================================
// SEARCH SUB-COMPONENT (for subheader)
// =====================================================================================

function SubheaderSearch({ searchTerm, onSearchChange, matchCount }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (searchTerm) setIsExpanded(true);
  }, [searchTerm]);

  React.useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleClose = () => {
    onSearchChange("");
    setIsExpanded(false);
  };

  // ✅ Fixed height for both states to prevent layout shift
  const fixedHeight = 36;

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 14px",
          height: fixedHeight,
          background: "rgba(255,255,255,0.08)",
          borderRadius: THEME.radius,
          border: "1px solid rgba(255,255,255,0.12)",
          cursor: "pointer",
          transition: "all 0.15s ease",
          color: "rgba(255,255,255,0.7)",
          fontSize: FONTS.sm,
          fontFamily: FONTS.family,
          boxSizing: "border-box",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = THEME.primary;
          e.currentTarget.style.background = "rgba(255,255,255,0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <span>Find</span>
      </button>
    );
  }

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 14px",
      height: fixedHeight,
      background: "rgba(255,255,255,0.1)",
      borderRadius: THEME.radius,
      border: `1px solid ${THEME.primary}`,
      boxShadow: `inset 0 0 0 1px ${THEME.primary}30`,
      minWidth: 220,
      boxSizing: "border-box",
    }}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={THEME.primary}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>

      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Find in view..."
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          fontSize: FONTS.sm,
          fontFamily: FONTS.family,
          background: "transparent",
          color: "white",
          minWidth: 100,
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleClose();
        }}
      />

      {searchTerm && matchCount > 0 && (
        <span style={{
          fontSize: FONTS.xs,
          color: THEME.dark,
          background: THEME.primary,
          padding: "2px 8px",
          borderRadius: 4,
          fontWeight: FONTS.semibold,
        }}>
          {matchCount}
        </span>
      )}

      <button
        onClick={handleClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: 16,
          color: "rgba(255,255,255,0.6)",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// =====================================================================================
// MODE SELECTOR SUB-COMPONENT
// =====================================================================================

function ModeSelector({ baseParams, modeLabel, tableNames }) {
  const { getLocksForView } = useMatchLock();

  const modeGroup = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  };

  const modeLabelPill = {
    background: "transparent",
    border: "none",
    padding: "0 4px",
    opacity: 0.5,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: "white",
    fontWeight: FONTS.regular,
    fontSize: FONTS.xs,
    whiteSpace: "nowrap",
    fontFamily: FONTS.family,
  };

  // Teal badge style for active, subtle for inactive (matching venue badge)
  const modeBtn = (active, locked) => ({
    border: active
      ? (locked ? "1px solid rgba(251, 191, 36, 0.4)" : "1px solid rgba(47, 214, 181, 0.4)")
      : (locked ? "1px solid rgba(251, 191, 36, 0.25)" : "1px solid rgba(255,255,255,0.15)"),
    background: active
      ? (locked ? "rgba(251, 191, 36, 0.15)" : "rgba(47, 214, 181, 0.15)")
      : "transparent",
    color: active
      ? (locked ? "#fbbf24" : "#2fd6b5")
      : (locked ? "rgba(251, 191, 36, 0.7)" : "rgba(255,255,255,0.7)"),
    borderRadius: "6px",
    padding: "6px 14px",
    cursor: "pointer",
    fontWeight: active ? 600 : FONTS.medium,
    fontSize: 12,
    fontFamily: FONTS.family,
    boxSizing: "border-box",
    textDecoration: "none",
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  });

  const currentMode = useMemo(() => {
    const mode = baseParams.get("mode")?.toLowerCase() || "out";
    return mode;
  }, [baseParams]);

  // Get current view name from URL params for lock lookup
  const currentView = baseParams.get("view") || "";
  const viewLocks = getLocksForView(currentView);

  const buildUrl = (mode, view = null) => {
    const p = new URLSearchParams(baseParams.toString());
    p.set("mode", mode);

    const cv = p.get("view") || "";
    const isCurrentlyTeamView = cv.toLowerCase().includes("team list");

    if (mode === "team") {
      p.set("view", DEFAULT_TEAM_VIEW);
      p.delete("fixture");
    } else if (isCurrentlyTeamView) {
      p.delete("view");
    } else if (view) {
      p.set("view", view);
    }

    return `?${p.toString()}`;
  };

  // const isInGame — tab hidden, replaced by TVC Playlist
  const isOut = currentMode === "out" || currentMode === "o";
  // const isLed — tab hidden, replaced by In-Game LED Playlist
  const isTeam = currentMode === "team" || currentMode === "t";
  const isIptv = currentMode === "iptv" || currentMode === "p";
  const isTvc = currentMode === "tvc";
  const isCommLed = currentMode === "commled" || currentMode === "comm-led" || currentMode === "ledplaylist" || currentMode === "led-playlist";

  if (!tableNames) {
    return (
      <div style={modeGroup}>
        <span style={modeLabelPill}>Loading...</span>
      </div>
    );
  }

  const lockIcon = (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );

  return (
    <div style={modeGroup}>
      <span style={modeLabelPill}>
        <span style={{ opacity: 0.6 }}>Mode:</span>
      </span>
      <a href={buildUrl("out")} style={modeBtn(isOut, viewLocks.out)}>
        {viewLocks.out && lockIcon}{tableNames.out}
      </a>
      {/* TVC Content (ingame) tab hidden — replaced by TVC Playlist */}
      {/* In-Game LED Commercial (entitlements) tab hidden — replaced by In-Game LED Playlist */}
      <a href={buildUrl("team")} style={modeBtn(isTeam, viewLocks.team)}>
        {viewLocks.team && lockIcon}{tableNames.team}
      </a>
      <a href={buildUrl("iptv")} style={modeBtn(isIptv, viewLocks.iptv)}>
        {viewLocks.iptv && lockIcon}{tableNames.iptv}
      </a>
      <a href={buildUrl("tvc")} style={modeBtn(isTvc, viewLocks.tvc)}>
        {viewLocks.tvc && lockIcon}TVC Playlist
      </a>
      <a href={buildUrl("commled")} style={modeBtn(isCommLed, viewLocks.commled)}>
        {viewLocks.commled && lockIcon}In-Game LED Playlist
      </a>
    </div>
  );
}
